/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import { PHASE, SESSION_PROPERTY } from './constants';
import { standby } from './standby';
import { terminate } from './terminate';
import { dispose } from './dispose';

export default {
  name: 'requirement155',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      systemId: '',
      sessionProperty: SESSION_PROPERTY.DEFAULT,
      modal_requirement_155: false,
      adjustmentStatus: '',
      horizontalStatus: '',
      verticalStatus: '',
      readoutData: [],
      successReadValue: '',
      ngReadValue1: '',
      ngReadValue2: '',
      routineStatus1: '',
      routineStatus2: '',
      exitPromise: null,
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.ADJUSTMENT_NG:
        case PHASE.ADJUSTMENT_NO:
        case PHASE.FAILED:
        case PHASE.ERROR:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.EXECUTION:
        case PHASE.START:
        default:
          return 'secondary';
      }
    },

    /**
     * ダイアログのヘッダー文言
     */
    headerTitle() {
      // 待機
      const startTitle = 'message.common_title_confirmation';
      // 待機, 実行中
      const ExecutionTitle = 'individual.common_title_execution';
      // 正常終了
      const SuccessTitle = 'message.common_title_success';
      // 中止失敗, 共通エラー
      const ErrorTitle = 'message.common_title_error';
      const titleMap = {
        start: startTitle,
        execution: ExecutionTitle,
        success: SuccessTitle,
        adjustment_ng: ErrorTitle,
        adjustment_no: ErrorTitle,
        error: ErrorTitle,
        failed: ErrorTitle
      };
      return this.$t(titleMap[this.phase]);
    },

    /** 表示メッセージ */
    workSupportMessage() {
      const messageMap = {
        start: `${this.i18nWsKey}.REQ_WS_155_TXT_1`,
        execution: `${this.i18nWsKey}.REQ_WS_155_TXT_2`,
        success: `${this.i18nWsKey}.REQ_WS_155_TXT_3_0000`,
        adjustment_ng: `${this.i18nWsKey}.REQ_WS_155_TXT_4`,
        adjustment_no: `${this.i18nWsKey}.REQ_WS_155_TXT_5`,
        failed: `${this.i18nWsKey}.REQ_WS_155_TXT_6`,
        error: `individual.error_communication_error`
      };
      switch (this.phase) {
        case PHASE.SUCCESS:
          return this.$t(messageMap[this.phase]).replace('$DID$', this.successReadValue);
        case PHASE.ADJUSTMENT_NG:
          return this.$t(messageMap[this.phase]).replace('$DID1$', this.ngReadValue1).replace('$DID2$', this.ngReadValue2).replace('$ROUTINESTATUS1$', this.routineStatus1).replace('$ROUTINESTATUS2$', this.routineStatus2);
        case PHASE.FAILED:
          return this.$t(messageMap[this.phase]).replace('$DID$', this.failedReadValue1).replace('$DID$', this.failedReadValue2).replace('$DID$', this.failedReadValue3);
        default:
          break;
      }
      return this.$t(messageMap[this.phase]);
    },

    // 「OK」ボタンの表示確認
    showButtonOk() {
      const showOkButtonMap = {
        start: true,
        success: true,
        adjustment_ng: true,
        adjustment_no: true,
        failed: true,
        error: true
      };
      let result = showOkButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },

    // 「キャンセル」ボタンの表示判定
    showButtonCancel() {
      const showCancelButtonMap = {
        start: true,
      };
      let result = showCancelButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_155 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      this.sessionProperty = SESSION_PROPERTY.DEFAULT;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_155 = false;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     * @param {string} calledFrom 呼び出し元
     */
    async validateErrorResult(result, calledFrom = '') {
      await apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        async (result) => {
          result;
          // terminate,disposeから呼ばれた場合は共通エラーに遷移しない
          if (calledFrom === 'terminate' || calledFrom === 'dispose') return;
          await this.showUnexpectedError();
        },
      );
    },

    /** その他エラー発生時の表示 */
    async showUnexpectedError() {
      // スピナー消えてしまうのを防ぐためPromiseを保持し、各API失敗時に解決を待つ
        this.exitPromise = this.sessionProperty === SESSION_PROPERTY.NON_DEFAULT ? terminate(this) : dispose(this);
      await this.exitPromise;
      // 異常終了：エラー表示
      this.phase = PHASE.ERROR;
    },

    /** 「OK」ボタンの押下処理 */
    onOkClicked() {
      switch (this.phase) {
        case 'start':
          this.onConfirmOkClicked();
          break;
        case 'success':
        case 'adjustment_ng':
        case 'adjustment_no':
        case 'failed':
        case 'error':
          // 機能終了
          this.closeWizard();
          break;
        default:
          break;
      }
    },

    /** 待機画面「OK」押下 */
    async onConfirmOkClicked() {
      // 開始処理のAPI実行
      standby(this);
    },
  },

  /**
   * DOM作成後実行処理
   */
  async mounted() {
    this.init();
  },
};